import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Badge({ children, tone = 'neutral', className }) {
    return (_jsx("span", { className: className, "data-badge": true, style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            borderRadius: 'var(--radius-chip)',
            border: `1px solid ${tone === 'accent' ? 'var(--accent)' : 'var(--border-strong)'}`,
            color: tone === 'accent' ? 'var(--accent)' : 'var(--text-muted)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
            padding: '1px 8px',
            whiteSpace: 'nowrap',
        }, children: children }));
}
// T8R2-6: the amber mark (--warning) is the border; the text uses --warning-text (>= 4.5:1 in light mode).
const SEVERITY_META = {
    critical: { glyph: '✕', color: 'var(--critical)', label: 'Critical' },
    high: { glyph: '⚠', color: 'var(--warning)', text: 'var(--warning-text)', label: 'High' },
    elevated: { glyph: '△', color: 'var(--warning)', text: 'var(--warning-text)', label: 'Elevated' },
    moderate: { glyph: '●', color: 'var(--secondary)', label: 'Moderate' },
    standard: { glyph: 'ℹ', color: 'var(--text-dim)', label: 'Standard' },
};
export function SeverityBadge({ severity, className }) {
    const m = SEVERITY_META[severity];
    return (_jsxs("span", { className: className, "data-severity-badge": true, "data-severity": severity, style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            borderRadius: 'var(--radius-chip)',
            border: `1px solid ${m.color}`,
            color: m.text ?? m.color,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 600,
            padding: '1px 8px',
            whiteSpace: 'nowrap',
        }, "aria-label": m.label, children: [_jsx("span", { "aria-hidden": "true", children: m.glyph }), m.label] }));
}
const STATUS_META = {
    done: { glyph: '✓', color: 'var(--success)', label: 'Done' },
    in_progress: { glyph: '◐', color: 'var(--warning)', label: 'In progress' },
    todo: { glyph: '○', color: 'var(--critical)', label: 'To do' },
    blocked: { glyph: '✕', color: 'var(--critical)', label: 'Blocked' },
    review_needed: { glyph: '⬤', color: 'var(--accent)', label: 'Review needed' },
    delegate_up: { glyph: '↑', color: 'var(--text-dim)', label: 'Delegate up' },
    not_applicable: { glyph: '—', color: 'var(--text-dim)', label: 'N/A' },
};
/**
 * UC-4 Alerts category tile (PR-P3-2). Reuses existing semantic chip
 * pairs — only `litigation`/`litigationBg` are net-new (breach already
 * owns alarm-red, legislation already owns citation-purple; see
 * `.claude/skills/ux-adversary/runs/uc-4-alerts/design-rationale.md`
 * §"New DS tokens"). Per UC-4 R2 H7/H21: this tile IS the category
 * signal — there is no separate `.cat-chip`, and it renders
 * `aria-hidden` since the visible label + `<SeverityBadge>` carry the
 * accessible name (H10).
 */
const CATEGORY_META = {
    legislation: { glyph: '§', fg: 'var(--regulatory)', bg: 'var(--regulatory-bg)', label: 'Legislation' },
    litigation: { glyph: '⚖', fg: 'var(--litigation)', bg: 'var(--litigation-bg)', label: 'Litigation' },
    framework: { glyph: '☰', fg: 'var(--secondary-fg)', bg: 'var(--secondary-bg)', label: 'Framework revision' },
    breach: { glyph: '⚠', fg: 'var(--danger-fg)', bg: 'var(--danger-bg)', label: 'Breach intelligence' },
    cert: { glyph: '⏱', fg: 'var(--warning-fg)', bg: 'var(--warning-bg)', label: 'Cert deadline' },
    'assessor-question': { glyph: '?', fg: 'var(--secondary-fg)', bg: 'var(--secondary-bg)', label: 'Assessor question' },
};
export function CategoryIcon({ category, className }) {
    const m = CATEGORY_META[category];
    return (_jsx("span", { className: className, "data-category-icon": true, "data-category": category, "aria-hidden": "true", style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            flex: '0 0 auto',
            borderRadius: 'var(--radius-chip)',
            background: m.bg,
            color: m.fg,
            fontSize: 'var(--font-size-2xs)',
            fontWeight: 600,
            lineHeight: 1,
        }, children: m.glyph }));
}
export function alertCategoryLabel(category) {
    return CATEGORY_META[category].label;
}
export function StatusBadge({ status, className }) {
    const m = STATUS_META[status];
    return (_jsxs("span", { className: className, "data-status-badge": true, "data-status": status, style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: m.color,
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            fontWeight: 500,
            whiteSpace: 'nowrap',
        }, "aria-label": m.label, children: [_jsx("span", { "aria-hidden": "true", children: m.glyph }), m.label] }));
}
//# sourceMappingURL=Badge.js.map